<template>
    <div
        class="user-avatar"
        :class="{ 'has-image': hasImage }"
        :style="avatarStyle"
    >
        <AnimatePresence>
            <Motion
                v-if="imageUrl && !imageError"
                :key="'image'"
                :initial="{ opacity: 0, scale: 0.8 }"
                :animate="{ opacity: 1, scale: 1 }"
                :exit="{ opacity: 0, scale: 0.8 }"
                :transition="{
                    type: 'spring',
                    stiffness: 300,
                    damping: 25,
                }"
                class="avatar-content"
            >
                <img
                    :src="imageUrl"
                    alt=""
                    class="avatar-image"
                    @error="handleImageError"
                    @load="onImageLoad"
                />
            </Motion>
            <Motion
                v-else
                :key="'initials'"
                :initial="{ opacity: 0, scale: 0.8 }"
                :animate="{ opacity: 1, scale: 1 }"
                :exit="{ opacity: 0, scale: 0.8 }"
                :transition="{
                    type: 'spring',
                    stiffness: 300,
                    damping: 25,
                }"
                class="avatar-content"
            >
                <div
                    class="avatar-initials"
                    :style="{ fontSize: `${size * 0.4}px` }"
                >
                    {{ initials }}
                </div>
            </Motion>
        </AnimatePresence>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, watch } from "vue";
    import { Motion, AnimatePresence } from "motion-v";
    import type { SubscriptionPlan } from "@/types/Subscription";

    interface Props {
        name: string;
        imageUrl?: string | null;
        size?: number;
        plan?: SubscriptionPlan | null;
    }

    const props = withDefaults(defineProps<Props>(), {
        imageUrl: null,
        size: 120,
        plan: null,
    });

    const imageError = ref(false);
    const imageLoaded = ref(false);

    // Reset error and loaded state when imageUrl changes
    watch(
        () => props.imageUrl,
        () => {
            imageError.value = false;
            imageLoaded.value = false;
        },
    );

    const hasImage = computed(() => {
        return !!props.imageUrl && !imageError.value;
    });

    const placeholderGradient = computed(() => {
        const plan = props.plan;
        const gradients: Record<NonNullable<typeof plan>, string> = {
            basis: "linear-gradient(135deg, #475569 0%, #64748b 50%, #94a3b8 100%)",
            plus: "linear-gradient(135deg, #b45309 0%, #d97706 50%, #fbbf24 100%)",
            pro: "linear-gradient(135deg, #4033e6 0%, #3477ff 50%, #a551f5 100%)",
        };
        return plan
            ? gradients[plan]
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
    });

    const avatarStyle = computed(() => ({
        width: `${props.size}px`,
        height: `${props.size}px`,
        ...(hasImage.value ? {} : { background: placeholderGradient.value }),
    }));

    const initials = computed(() => {
        if (!props.name) return "?";

        const names = props.name.trim().split(" ");
        if (names.length === 1) {
            return names[0].charAt(0).toUpperCase();
        }

        const firstName = names[0].charAt(0);
        const lastName = names[names.length - 1].charAt(0);
        return (firstName + lastName).toUpperCase();
    });

    const handleImageError = () => {
        imageError.value = true;
    };

    const onImageLoad = () => {
        imageLoaded.value = true;
    };
</script>

<style scoped lang="scss">
    .user-avatar {
        position: relative;
        border-radius: 50%;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        transition: background 0.3s ease;

        &.has-image {
            background: var(--color-white);
        }

        .avatar-content {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            will-change: transform, opacity;
        }

        .avatar-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .avatar-initials {
            color: var(--color-always-white);
            font-weight: 700;
            user-select: none;
            text-transform: uppercase;
        }
    }
</style>
